import {Dialog} from '@primer/react'
import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePaths} from '../contexts/PathsContext'
import {useButton} from '../contexts/ButtonContext'
import {IsLoadingContext} from '../contexts/IsLoadingContext'
import {UpdatePreviewsProvider} from '../contexts/UpdatePreviewsContext'
import {useEditablePreviews} from '../hooks/use-editable-previews'
import {PreviewsTable} from './previews-table'
import {EnforcementPicker} from './enforcement-picker'
import {PreviewsCountSummary} from './previews-count-summary'
import type {PreviewPayload} from '../types'

type DialogWrapperProps = {
  onClose: React.ComponentProps<typeof Dialog>['onClose']
  onSuccess: () => void
}

export function DialogWrapper({onClose, onSuccess}: DialogWrapperProps) {
  const {buttonRef} = useButton()
  const {previewUrl} = usePaths()
  const {editablePreviews: previews, initializePreviews, setRulesetName, togglePreview} = useEditablePreviews()
  const {isFetching} = useQuery({
    queryKey: [previewUrl],
    queryFn: async () => {
      const response = await reactFetchJSON(previewUrl)

      if (response.ok) {
        try {
          const data = (await response.json()) as PreviewPayload
          initializePreviews(data)
          return data
        } catch {
          throw new Error('Failed to load previews.')
        }
      } else if (response.status === 408) {
        throw new Error('Your request timed out.')
      } else {
        throw new Error('Failed to load previews.')
      }
    },
    enabled: !!previewUrl,
  })

  async function handleCreateRulesets() {
    const createUrl = previewUrl.replace('/preview', '/create')
    const response = await reactFetchJSON(createUrl, {
      method: 'POST',
      body: previews,
    })

    if (response.ok) {
      onSuccess()
    } else {
      throw new Error('Failed to create rulesets.')
    }
  }

  return (
    <Dialog
      title="Review import"
      width="large"
      height="large"
      returnFocusRef={buttonRef}
      onClose={onClose}
      footerButtons={[
        {
          buttonType: 'default',
          content: 'Cancel',
          onClick: _ev => onClose('close-button'),
        },
        {
          buttonType: 'primary',
          content: 'Create rulesets',
          loading: isFetching,
          onClick: _ev => handleCreateRulesets(),
        },
      ]}
    >
      <Dialog.Body>
        <IsLoadingContext value={isFetching}>
          <UpdatePreviewsProvider togglePreview={togglePreview} setRulesetName={setRulesetName}>
            <div className="d-flex flex-column gap-3">
              <PreviewsCountSummary previews={previews} />
              <EnforcementPicker />
              <PreviewsTable previews={previews} />
            </div>
          </UpdatePreviewsProvider>
        </IsLoadingContext>
      </Dialog.Body>
    </Dialog>
  )
}
